import { cleanCnpjOrCpf, formatCaepf, formatCnpj } from '@/shared/utils/document.util';

export function formatCurrencyBRL(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function parseCurrencyInput(raw: string): number | null {
  const digits = (raw ?? '').replace(/\D/g, '');
  if (!digits) return null;
  return parseInt(digits, 10) / 100;
}

export function formatCurrencyInput(raw: string): string {
  const cents = parseCurrencyInput(raw);
  if (cents == null) return '';
  return cents.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Casas decimais aceitas nos campos de PROPORÇÃO de cobertura do PME. */
const PERCENT_DECIMALS = 2;

/**
 * Higieniza o texto digitado num campo de PROPORÇÃO: mantém só dígitos e um
 * único separador decimal (vírgula; ponto é aceito e convertido), com no
 * máximo {@link PERCENT_DECIMALS} casas. Preserva estados intermediários da
 * digitação (ex.: "10," ou "10,5") para o input controlado não "engolir" a
 * vírgula.
 */
export function sanitizePercentInput(raw: string): string {
  const cleaned = (raw ?? '').replace(/\./g, ',').replace(/[^\d,]/g, '');
  const [intPart, ...rest] = cleaned.split(',');
  if (rest.length === 0) return intPart;
  return `${intPart},${rest.join('').slice(0, PERCENT_DECIMALS)}`;
}

/**
 * Percentual (não-negativo, até 2 casas decimais) dos campos de PROPORÇÃO de
 * cobertura proporcional do PME. NÃO limita a 100: o produto InsureMO usa
 * proporções acima de 100% — ex.: "Invalidez Permanente Total ou Parcial por
 * Acidente" (8200003) é 200% de "Morte Acidental". A validação (só rejeita
 * negativo) é feita no `coverage-resolver`. Os campos de
 * corretagem/agenciamento do Step 4 têm parser próprio (com teto de 100%),
 * não usam este.
 */
export function parsePercentInput(raw: string): number {
  const normalized = sanitizePercentInput(raw).replace(',', '.');
  const value = Number.parseFloat(normalized);
  return Number.isFinite(value) ? value : 0;
}

/** Formata uma proporção para exibição pt-BR (ex.: 10.5 → "10,5"), sem
 *  separador de milhar e com no máximo 2 casas decimais. */
export function formatPercentValue(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '';
  return value.toLocaleString('pt-BR', {
    useGrouping: false,
    maximumFractionDigits: PERCENT_DECIMALS,
  });
}

/**
 * Limita um percentual digitado ao teto da cobertura (o `Proportion` do
 * schema). Quando a cobertura não define um teto válido (> 0), não limita.
 * Ex.: "Invalidez Permanente Total ou Parcial por Acidente" tem Proportion=200
 * → o corretor pode digitar no máximo 200%.
 */
export function clampPercentToMax(value: number, max?: number | null): number {
  return max != null && Number.isFinite(max) && max > 0
    ? Math.min(value, max)
    : value;
}

/**
 * Canonicaliza um documento (CNPJ ou CAEPF) para a string exatamente no
 * formato da máscara, **sempre re-derivando a partir dos dígitos crus**.
 *
 * Isto é o que evita o bug do `use-mask-input`: quando devolvemos um valor
 * já mascarado (e possivelmente corrompido por uma máscara anterior) de volta
 * ao input controlado, a biblioteca reprocessa os separadores e "embaralha"
 * os dígitos. Reconstruindo a máscara a partir só dos números garantimos um
 * valor que encaixa perfeitamente — o mesmo padrão do fluxo de edição
 * (`formatRegisterNumber`).
 *
 * - CNPJ:  `XX.XXX.XXX/XXXX-XX` (14 dígitos)
 * - CAEPF: `XXX.XXX.XXX/XXX-XX` (14 dígitos)
 *
 * Valores parciais (ainda sendo digitados) são devolvidos só com dígitos,
 * deixando a máscara do input formatar conforme o usuário digita.
 */
export function formatGroupLifeDocument(
  value: string | null | undefined,
  isCaepf: boolean,
): string {
  if (isCaepf) {
    // CAEPF é numérico puro — mantém o comportamento anterior.
    const digits = (value ?? '').replace(/\D/g, '').slice(0, 14);
    if (digits.length < 14) return digits;
    return formatCaepf(digits);
  }
  // CNPJ pode ser ALFANUMÉRICO (novo formato RFB): 12 caracteres A-Z/0-9 nos
  // 12 primeiros + 2 dígitos verificadores. Numérico legado é subset.
  const chars = cleanCnpjOrCpf(value).slice(0, 14);
  if (chars.length < 14) return chars;
  return formatCnpj(chars);
}
